import { Link as RouterLink } from 'react-router-dom'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import { ItemMasterRow } from './hooks/useItemMastersQuery'
import { isItemMasterComplete } from './utils'

function formatCurrency( amount: number | string | null | undefined, currencyCode = 'USD' ): string {
	if ( amount === null || amount === undefined ) {
		return ''
	}
	const num = Number( amount )
	if ( isNaN( num ) ) {
		return ''
	}
	return new Intl.NumberFormat( 'en-US', {
		style: 'currency',
		currency: currencyCode,
		maximumFractionDigits: 4,
		minimumFractionDigits: 2,
	} ).format( num )
}

export default function getColumns(): GridColDef[] {
	return [
		{
			field: 'product',
			headerName: 'Name',
			flex: 1,
			sortable: false,
			valueGetter: ( _value: any, row: ItemMasterRow ) => {
				const base = row.variants.find( ( v ) => v.isBase )
				return base?.product?.name ?? ''
			},
			renderCell: ( params: GridRenderCellParams<ItemMasterRow, string> ) => (
				<Link
					component={RouterLink}
					to={`/manage/item-masters/${params.row.id}`}
					underline="hover"
					color="primary"
				>
					{params.value}
				</Link>
			),
		},
		{
			field: 'manufacturer',
			headerName: 'Manufacturer',
			width: 200,
			sortable: false,
			valueGetter: ( _value: any, row: ItemMasterRow ) => {
				const base = row.variants.find( ( v ) => v.isBase )
				return base?.product?.manufacturer?.name ?? ''
			},
		},
		{
			field: 'preferredVendor',
			headerName: 'Preferred Vendor',
			width: 200,
			sortable: false,
			valueGetter: ( _value: any, row: ItemMasterRow ) => {
				const base = row.variants.find( ( v ) => v.isBase )
				return base?.product?.preferredVendor?.name ?? ''
			},
		},
		{
			field: 'msrp',
			headerName: 'MSRP',
			width: 120,
			sortable: false,
			valueGetter: ( _value: any, row: ItemMasterRow ) => {
				const base = row.variants.find( ( v ) => v.isBase )
				return base?.product?.msrp ?? null
			},
			valueFormatter: ( value: any ) => formatCurrency( value ),
		},
		{
			field: 'listPrice',
			headerName: 'List Price',
			width: 120,
			sortable: false,
			valueGetter: ( _value: any, row: ItemMasterRow ) => {
				const base = row.variants.find( ( v ) => v.isBase )
				const sp = base?.product && 'standardPrice' in base.product ? base.product.standardPrice : null
				return sp ? formatCurrency( sp.amount, sp.currencyCode ) : ''
			},
		},
		{
			field: 'isActive',
			headerName: 'Status',
			width: 120,
			sortable: false,
			valueGetter: ( _value: any, row: ItemMasterRow ) => row.isActive ? 'Active' : 'Inactive',
		},
		{
			field: 'completeness',
			headerName: 'Completeness',
			width: 140,
			sortable: false,
			valueGetter: ( _value: any, row: ItemMasterRow ) =>
				isItemMasterComplete( row.variants ) ? 'Complete' : 'Draft',
			renderCell: ( params: GridRenderCellParams<ItemMasterRow, string> ) => {
				if ( params.value === 'Complete' ) {
					return <Chip label="Complete" color="success" size="small" variant="outlined" />
				}
				return (
					<Tooltip title="Missing weight or dimensions. Add them to complete this item master.">
						<Chip label="Draft" color="default" size="small" />
					</Tooltip>
				)
			},
		},
	]
}
